import { useState, useMemo } from 'react';
import { STATE_COORDINATES } from '../utils/mapboxUtils';
import { STATE_NAMES } from '../constants/stateNames';
import { useStateCities } from './useStateCities';

const ALL_STATE_CODES = Object.keys(STATE_COORDINATES).sort();

interface UseEditRegionPanelProps {
	searchQuery: string;
	onSearchChange: (q: string) => void;
}

export const useEditRegionPanel = ({ searchQuery, onSearchChange }: UseEditRegionPanelProps) => {
	const [expandedStateCode, setExpandedStateCode] = useState<string | null>(null);
	const [deselectedCities, setDeselectedCities] = useState<Map<string, Set<number>>>(new Map());

	const { cities, loading } = useStateCities(expandedStateCode);

	const filteredStates = useMemo(
		() =>
			ALL_STATE_CODES.filter(
				(c) =>
					c.toLowerCase().includes(searchQuery.toLowerCase()) ||
					(STATE_NAMES[c] ?? '').toLowerCase().includes(searchQuery.toLowerCase())
			),
		[searchQuery]
	);

	const filteredCities = useMemo(
		() => cities.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())),
		[cities, searchQuery]
	);

	const handleExpand = (e: React.MouseEvent, code: string) => {
		e.stopPropagation();
		onSearchChange('');
		setExpandedStateCode(code);
	};

	const toggleCity = (cityId: number) => {
		if (!expandedStateCode) return;
		const stateCode = expandedStateCode;
		setDeselectedCities((prev) => {
			const newMap = new Map(prev);
			const deselected = new Set(newMap.get(stateCode) ?? []);
			if (deselected.has(cityId)) {
				deselected.delete(cityId);
			} else {
				deselected.add(cityId);
			}
			newMap.set(stateCode, deselected);
			return newMap;
		});
	};

	const isCitySelected = (cityId: number) =>
		!deselectedCities.get(expandedStateCode!)?.has(cityId);

	const allFilteredSelected =
		filteredCities.length > 0 && filteredCities.every((c) => isCitySelected(c.id));
	const someFilteredSelected = filteredCities.some((c) => isCitySelected(c.id));

	const toggleAllCities = () => {
		if (!expandedStateCode) return;
		const stateCode = expandedStateCode;
		setDeselectedCities((prev) => {
			const newMap = new Map(prev);
			const deselected = new Set(newMap.get(stateCode) ?? []);
			if (allFilteredSelected) {
				filteredCities.forEach((c) => deselected.add(c.id));
			} else {
				filteredCities.forEach((c) => deselected.delete(c.id));
			}
			newMap.set(stateCode, deselected);
			return newMap;
		});
	};

	const handleBack = () => {
		onSearchChange('');
		setExpandedStateCode(null);
	};

	return {
		expandedStateCode,
		filteredStates,
		filteredCities,
		loading,
		allFilteredSelected,
		someFilteredSelected,
		handleExpand,
		toggleCity,
		isCitySelected,
		toggleAllCities,
		handleBack,
	};
};
